import React, {useEffect, useState} from "react";
import {SubCompanyOnboardingStatuses} from "../../../constants/constants";
import {Button, message, Modal, Checkbox} from "antd";
import {OnboardingStatusButton} from "./ChangeSubcompanyOnboardingStatusInput.styles";
import {Select, Input, Form} from "antd";
import {useFormik} from "formik";
import * as Yup from "yup";
import {useStore} from "../../../hooks/useStore";
import {useTranslation} from "react-i18next";
import {ZoneSelect} from "../ZoneSelect";

export const ChangeSubcompanyOnboardingStatusInput = ({
    subCompany,
    onChange = () => {},
    recall = () => {}
}) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [companyIdToTransfer, setCompanyIdToTransfer] = useState("");
    const [isTransferring, setIsTransferring] = useState(false);
    const [wantToTransfer, setWantToTransfer] = useState(false);
    const [showConfirmTransfer, setShowConfirmTransfer] = useState(false);
    const [companyIdError, setCompanyIdError] = useState("");

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleOk = () => {
        setIsModalVisible(false);
        recall();
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleTransferModalCancel = () => {
        setShowTransferModal(false);
        setWantToTransfer(false);
        setCompanyIdToTransfer("");
        setCompanyIdError("");
    };

    const handleConfirmTransferCancel = () => {
        setShowConfirmTransfer(false);
    };

    const handleConfirmTransferOk = () => {
        setShowConfirmTransfer(false);
        performTransfer();
    };

    const handleTransferModalSubmit = async () => {
        // Reset error state
        setCompanyIdError("");

        // Check if ID is provided
        if (!companyIdToTransfer) {
            setCompanyIdError("Firma ID'si zorunludur");
            return;
        }

        // Validate ID format (24 hex characters)
        const hexRegex = /^[0-9a-fA-F]{24}$/;
        if (!hexRegex.test(companyIdToTransfer)) {
            setCompanyIdError(
                "Firma ID'si 24 karakter uzunluğunda olmalı ve sadece geçerli karakterler içermelidir"
            );
            return;
        }

        setShowConfirmTransfer(true);
    };

    const formik = useFormik({
        initialValues: {
            onboardingStatus: subCompany?.onboardingStatus,
            note: "",
            zoneId: subCompany?.zoneInfo?.zoneId
        },
        validationSchema: Yup.object({
            onboardingStatus: Yup.string().required("Durum zorunlu"),
            note: Yup.string().when("onboardingStatus", {
                is: val => val === "pending" || val === "rejected",
                then: Yup.string().required("Not zorunlu"),
                otherwise: Yup.string()
            })
        }),
        enableReinitialize: true,
        onSubmit: async values => {
            const payload = {
                _id: subCompany?._id,
                onboardingStatus: values.onboardingStatus,
                note: values.note,
                zoneId: values?.zoneId
            };

            // Check if we need to show transfer modal
            if (
                values.onboardingStatus === "approved" &&
                !subCompany?.isCompanyTransferred &&
                wantToTransfer
            ) {
                setShowTransferModal(true);
                return;
            }

            const result = await stores.companyStore.changeSubCompanyOnboardingStatus(payload);
            if (result) {
                handleOk();
                formik.resetForm();
                setIsModalVisible(false);
                setWantToTransfer(false);
            } else {
                message.error("Bir hata oluştu. Lütfen tekrar deneyiniz.");
            }
        }
    });

    useEffect(() => {
        formik.resetForm();
        setWantToTransfer(false);
    }, [isModalVisible]);

    // Update local subCompany state to match the transferred state after successful transfer
    const [localSubCompany, setLocalSubCompany] = useState(subCompany);

    useEffect(() => {
        setLocalSubCompany(subCompany);
    }, [subCompany]);

    // Update local state when transfer is successful
    const performTransfer = async () => {
        setIsTransferring(true);

        try {
            const result = await stores.companyStore.transferSubcompanyToAnotherCompany({
                subCompanyId: localSubCompany?._id,
                companyId: companyIdToTransfer
            });

            if (result) {
                // Update local state to reflect transferred status
                setLocalSubCompany({
                    ...localSubCompany,
                    isCompanyTransferred: true
                });

                setShowTransferModal(false);
                setWantToTransfer(false);
                setCompanyIdToTransfer("");
                recall();
            }
        } catch (error) {
            if (
                error?.message?.includes(
                    "single String of 12 bytes or a string of 24 hex characters"
                )
            ) {
                message.error(
                    "Geçersiz firma ID formatı. Lütfen doğru ID girdiğinizden emin olun."
                );
            } else {
                message.error("Transfer işlemi sırasında bir hata oluştu");
            }
        } finally {
            setIsTransferring(false);
        }
    };

    const handleChange = value => {
        onChange(value);
    };

    return (
        <>
            {localSubCompany?.onboardingStatus ? (
                <OnboardingStatusButton
                    status={localSubCompany?.onboardingStatus}
                    onClick={showModal}
                >
                    <span id="status-label">
                        {t(
                            `fields.subCompanyOnboardingOptions.${localSubCompany?.onboardingStatus}`,
                            {
                                ns: "common"
                            }
                        )}
                    </span>
                    <span id="action-label">{"Onboarding Durumunu Değiştir"}</span>
                </OnboardingStatusButton>
            ) : (
                <>-</>
            )}
            <Modal
                title="Durumu Değiştir"
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={null}
            >
                <Form layout="vertical" onFinish={formik.handleSubmit}>
                    {localSubCompany?.company?.companyName ? (
                        <Form.Item label="Firma Adı">
                            <Input
                                id="companyName"
                                name="companyName"
                                value={localSubCompany?.company?.companyName}
                                disabled
                            />
                        </Form.Item>
                    ) : (
                        <></>
                    )}
                    {localSubCompany?.name ? (
                        <Form.Item label="Alt Firma Adı">
                            <Input id="name" name="name" value={localSubCompany?.name} disabled />
                        </Form.Item>
                    ) : (
                        <></>
                    )}
                    {localSubCompany?.note ? (
                        <Form.Item label="Note">
                            <Input.TextArea
                                id="note"
                                name="note"
                                value={localSubCompany?.note}
                                disabled
                            />
                        </Form.Item>
                    ) : (
                        <></>
                    )}
                    <Form.Item label="Mevcut Durum">
                        <Select
                            id="currentStatus"
                            name="currentStatus"
                            placeholder="Seçiniz"
                            disabled
                            value={localSubCompany?.onboardingStatus}
                        >
                            {SubCompanyOnboardingStatuses.map(status => (
                                <Select.Option value={status.value} key={status.value}>
                                    {t(
                                        `fields.subCompanyOnboardingOptions.${status.translation_key}`,
                                        {ns: "common"}
                                    )}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Bölge">
                        <ZoneSelect
                            showTitle={false}
                            showSyUser={true}
                            marginBottom="0px"
                            cityName={localSubCompany?.city}
                            value={formik.values.zoneId}
                            onChange={value => {
                                handleChange(value);
                                formik.setFieldValue("zoneId", value);
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Durum"
                        validateStatus={
                            formik.touched.onboardingStatus && formik.errors.onboardingStatus
                                ? "error"
                                : ""
                        }
                        help={
                            formik.touched.onboardingStatus && formik.errors.onboardingStatus
                                ? formik.errors.onboardingStatus
                                : null
                        }
                    >
                        <Select
                            id="onboardingStatus"
                            name="onboardingStatus"
                            onChange={value => {
                                handleChange(value);
                                formik.setFieldValue("onboardingStatus", value);
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values.onboardingStatus}
                            placeholder="Seçiniz"
                        >
                            {SubCompanyOnboardingStatuses.map(status => (
                                <Select.Option value={status.value} key={status.value}>
                                    {t(
                                        `fields.subCompanyOnboardingOptions.${status.translation_key}`,
                                        {ns: "common"}
                                    )}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {(formik.values.onboardingStatus === "pending" ||
                        formik.values.onboardingStatus === "rejected") && (
                        <Form.Item
                            label="Not"
                            validateStatus={
                                formik.touched.note && formik.errors.note ? "error" : ""
                            }
                            help={
                                formik.touched.note && formik.errors.note
                                    ? formik.errors.note
                                    : null
                            }
                        >
                            <Input.TextArea
                                id="note"
                                name="note"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.note}
                                rows={4}
                            />
                        </Form.Item>
                    )}
                    {formik.values.onboardingStatus === "approved" &&
                        !localSubCompany?.isCompanyTransferred && (
                            <Form.Item>
                                <Checkbox
                                    checked={wantToTransfer}
                                    onChange={e => setWantToTransfer(e.target.checked)}
                                >
                                    Transfer etmek istiyorum
                                </Checkbox>
                            </Form.Item>
                        )}
                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={formik.isSubmitting}>
                            Onayla
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Firma Transfer Et"
                visible={showTransferModal}
                onCancel={handleTransferModalCancel}
                footer={[
                    <Button key="back" onClick={handleTransferModalCancel}>
                        İptal
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleTransferModalSubmit}>
                        Transfer Et
                    </Button>
                ]}
            >
                <Form layout="vertical">
                    <Form.Item
                        label="Transfer Edilecek Firma ID"
                        validateStatus={companyIdError ? "error" : ""}
                        help={companyIdError}
                    >
                        <Input
                            value={companyIdToTransfer}
                            onChange={e => {
                                setCompanyIdToTransfer(e.target.value);
                                if (companyIdError) setCompanyIdError("");
                            }}
                            placeholder="Firma ID giriniz"
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Transfer Onayı"
                visible={showConfirmTransfer}
                onCancel={handleConfirmTransferCancel}
                footer={[
                    <Button key="back" onClick={handleConfirmTransferCancel}>
                        İptal
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={isTransferring}
                        onClick={handleConfirmTransferOk}
                    >
                        Eminim, Transfer Et
                    </Button>
                ]}
            >
                <p>Bu alt firmayı transfer etmek istediğinizden emin misiniz?</p>
            </Modal>
        </>
    );
};
